import React, { useMemo, useState } from 'react'
import {
  Box,
  Sheet,
  Table,
  Typography,
  Select,
  Option,
  Button
} from '@mui/joy'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'

export default function DataDisplayTable(props) {
  const { data = [], cols = [], onClickEntry, headerChildren } = props

  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sort, setSort] = useState({ by: undefined, dir: 'asc' })

  const visibleCols = useMemo(() => cols.filter(c => c.show !== false), [cols])

  const sortedData = useMemo(() => {
    if (!sort.by) return data
    const dirMul = sort.dir === 'asc' ? 1 : -1
    const accessor = sort.by
    const copy = [...data]
    copy.sort((a, b) => {
      const va = a?.[accessor]
      const vb = b?.[accessor]
      if (va == null && vb == null) return 0
      if (va == null) return -1 * dirMul
      if (vb == null) return 1 * dirMul
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dirMul
      const sa = String(va).toLowerCase()
      const sb = String(vb).toLowerCase()
      if (sa < sb) return -1 * dirMul
      if (sa > sb) return 1 * dirMul
      return 0
    })
    return copy
  }, [data, sort])

  const totalPages = Math.max(1, Math.ceil((sortedData.length || 0) / rowsPerPage))
  const startIndex = (page - 1) * rowsPerPage
  const endIndex = startIndex + rowsPerPage
  const paginatedData = useMemo(() => sortedData.slice(startIndex, endIndex), [sortedData, startIndex, endIndex])

  const getCellValue = (row, col) => {
    if (col.Cell && typeof col.Cell === 'function') return col.Cell({ value: row[col.accessor], row, original: row })
    if (col.render && typeof col.render === 'function') return col.render(row)
    return row[col.accessor]
  }

  const goPrev = () => setPage(p => Math.max(1, p - 1))
  const goNext = () => setPage(p => Math.min(totalPages, p + 1))

  const showingFrom = sortedData.length ? startIndex + 1 : 0
  const showingTo = sortedData.length ? Math.min(endIndex, sortedData.length) : 0

  const cycleSort = (accessor) => {
    setPage(1)
    setSort(prev => {
      if (prev.by !== accessor) return { by: accessor, dir: 'asc' }
      if (prev.dir === 'asc') return { by: accessor, dir: 'desc' }
      return { by: undefined, dir: 'asc' }
    })
  }

  const renderSortIcon = (accessor) => {
    if (sort.by !== accessor) return <UnfoldMoreIcon fontSize="small" />
    return sort.dir === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
  }

  return (
    <Box sx={{ width: '100%', pt: '4px' }}>
      <>
        {headerChildren}
      </>
      <Sheet variant="outlined" sx={{ borderRadius: 6 }}
      >
        <Table stickyHeader size="md" hoverRow>
          <thead>
            <tr>
              {visibleCols.map((col, idx) => (
                <th key={idx} style={{ textAlign: ['name', 'client', 'project', 'title'].includes(col.accessor) ? 'left' : 'center', fontWeight: 600, cursor: 'pointer', userSelect: 'none', height: '33px' }}
                  onClick={() => cycleSort(col.accessor)}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {col.Header || col.name || col.accessor}
                    {renderSortIcon(col.accessor)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rIdx) => (
              <tr className='data-display-table-tr'
                key={startIndex + rIdx}
                onClick={() => onClickEntry && onClickEntry(row)}
                style={{ cursor: onClickEntry ? 'pointer' : 'default' }}
              >
                {visibleCols.map((col, cIdx) => (
                  <td key={cIdx} style={{ textAlign: ['name', 'client', 'project', 'title'].includes(col.accessor) ? 'left' : 'center' }}>
                    {getCellValue(row, col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderTop: '1px solid', borderColor: 'divider' }}>
        {paginatedData?.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="body-sm">Rows per page</Typography>
              <Select size="sm" value={rowsPerPage} onChange={(_, v) => { setRowsPerPage(v); setPage(1) }} sx={{ minWidth: 80 }}>
                {[10, 20, 25, 50, 100].map(opt => (<Option key={opt} value={opt}>{opt}</Option>))}
              </Select>
            </Box>
        )}
          <Box sx={{ mb: 0.5, flexGrow: 1, textAlign: 'center' }}>
            <Typography level="body-sm" color="neutral">{`Showing ${showingFrom} to ${showingTo} of `}<Typography level="title-sm"> {`${sortedData.length}`}</Typography> {` records`}</Typography>
          </Box>
          {paginatedData?.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button className="btn-custom-primary-plain" size="sm" variant="plain" onClick={goPrev} disabled={page <= 1}>Previous</Button>
            <Typography level="body-sm">Page {page} of {totalPages}</Typography>
            <Button className="btn-custom-primary-plain" size="sm" variant="plain" onClick={goNext} disabled={page >= totalPages}>Next</Button>
          </Box>
           )}
        </Box>
       
      </Sheet>

      {props.tableChildren}
    </Box>
  )
}
